import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  FileText,
  CheckCircle2,
  Clock,
  XCircle,
  Send,
  Download,
  Eye,
  Users,
  Building2,
  DollarSign,
  Calculator,
  MessageSquare,
  Loader2,
  TrendingUp,
  Sparkles,
  Save
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'fluxoco_token';

const STATUS_CONFIG = {
  pendiente: { label: 'Pendiente', icon: Clock, color: 'text-yellow-500 bg-yellow-500/10' },
  aprobado: { label: 'Aprobado', icon: CheckCircle2, color: 'text-green-500 bg-green-500/10' },
  rechazado: { label: 'Rechazado', icon: XCircle, color: 'text-red-500 bg-red-500/10' }
};

// Giros con plantillas de módulos pre-seleccionados
const GIROS_CON_PLANTILLAS = [
  'Distribuidora / Flotilla',
  'Escuela / Colegio',
  'Taller mecánico / Servicios',
  'Cafetería / Restaurante',
  'Clínica / Consultorio',
  'Retail / Tienda',
  'Servicios profesionales',
  'Otro'
];

const ProspectosPage = () => {
  const [prospectos, setProspectos] = useState([]);
  const [stats, setStats] = useState(null);
  const [modulosConfig, setModulosConfig] = useState(null);
  const [plantillasGiro, setPlantillasGiro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isApproveOpen, setIsApproveOpen] = useState(false);
  const [selectedProspecto, setSelectedProspecto] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [approvalResult, setApprovalResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    nombre_negocio: '',
    giro: '',
    contacto_nombre: '',
    contacto_puesto: '',
    whatsapp_contacto: '',
    email: '',
    pais: 'México',
    moneda: 'MXN',
    usuarios_internos: 2,
    mensajes_dia_estimado: 50,
    modulos_seleccionados: ['pedidos_ventas'],
    integracion_externa: 'no'
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    Promise.all([
      fetchProspectos(),
      fetchStats(),
      fetchModulosConfig()
    ]);
  }, []);

  const fetchProspectos = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/prospectos`, {
        headers: getAuthHeaders()
      });
      setProspectos(response.data);
    } catch (error) {
      console.error('Error fetching prospectos:', error);
      toast.error('Error al cargar los prospectos');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/prospectos/stats`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchModulosConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/prospectos/config/modulos`, {
        headers: getAuthHeaders()
      });
      setModulosConfig(response.data);
      // También guardar plantillas por giro
      if (response.data.plantillas_giro) {
        setPlantillasGiro(response.data.plantillas_giro);
      }
    } catch (error) {
      console.error('Error fetching modulos config:', error);
    }
  };

  // Función para aplicar plantilla cuando cambia el giro
  const handleGiroChange = (giro) => {
    setFormData(prev => ({
      ...prev,
      giro: giro
    }));
    
    // Aplicar plantilla si existe
    if (plantillasGiro && plantillasGiro[giro]) {
      const plantilla = plantillasGiro[giro];
      setFormData(prev => ({
        ...prev,
        giro: giro,
        modulos_seleccionados: plantilla.modulos || ['pedidos_ventas'],
        usuarios_internos: plantilla.usuarios_sugeridos || 2,
        mensajes_dia_estimado: plantilla.mensajes_sugeridos || 50
      }));
      toast.info(`📋 Plantilla "${giro}" aplicada. Puedes ajustar los módulos manualmente.`);
    }
  };

  const handlePreview = async () => {
    if (!formData.nombre_negocio || !formData.contacto_nombre || !formData.whatsapp_contacto || !formData.email) {
      toast.error('Por favor completa los campos requeridos');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/prospectos/calcular`, formData, {
        headers: getAuthHeaders()
      });
      setPreviewData(response.data);
      setIsPreviewOpen(true);
    } catch (error) {
      console.error('Error calculating preview:', error);
      toast.error('Error al calcular la cotización');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/prospectos`, formData, {
        headers: getAuthHeaders()
      });
      toast.success('Cotización creada exitosamente');
      setIsCreateOpen(false);
      setIsPreviewOpen(false);
      resetForm();
      fetchProspectos();
      fetchStats();
    } catch (error) {
      console.error('Error creating prospecto:', error);
      toast.error('Error al crear la cotización');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (prospectoId) => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/prospectos/${prospectoId}/aprobar`, {}, {
        headers: getAuthHeaders()
      });
      setApprovalResult(response.data);
      setIsApproveOpen(true);
      toast.success('¡Prospecto aprobado! Tenant creado exitosamente');
      fetchProspectos();
      fetchStats();
    } catch (error) {
      console.error('Error approving prospecto:', error);
      toast.error(error.response?.data?.detail || 'Error al aprobar el prospecto');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async (prospectoId) => {
    try {
      await axios.post(`${API_URL}/api/prospectos/${prospectoId}/rechazar`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Prospecto rechazado');
      fetchProspectos();
      fetchStats();
    } catch (error) {
      console.error('Error rejecting prospecto:', error);
      toast.error('Error al rechazar el prospecto');
    }
  };

  const handleDownloadPDF = async (prospectoId, folio) => {
    try {
      const response = await axios.get(`${API_URL}/api/prospectos/${prospectoId}/pdf`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Cotizacion_${folio}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descargado');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Error al descargar el PDF');
    }
  };

  const handleSendWhatsApp = async (prospectoId) => {
    try {
      const response = await axios.post(`${API_URL}/api/prospectos/${prospectoId}/enviar-whatsapp`, {}, {
        headers: getAuthHeaders()
      });
      toast.info(response.data.message);
    } catch (error) {
      console.error('Error sending WhatsApp:', error);
      toast.error('Error al enviar por WhatsApp');
    }
  };

  const resetForm = () => {
    setFormData({
      nombre_negocio: '',
      giro: '',
      contacto_nombre: '',
      contacto_puesto: '',
      whatsapp_contacto: '',
      email: '',
      pais: 'México',
      moneda: 'MXN',
      usuarios_internos: 2,
      mensajes_dia_estimado: 50,
      modulos_seleccionados: ['pedidos_ventas'],
      integracion_externa: 'no'
    });
    setPreviewData(null);
  };

  const toggleModulo = (moduloKey) => {
    setFormData(prev => {
      const current = prev.modulos_seleccionados;
      if (current.includes(moduloKey)) {
        return { ...prev, modulos_seleccionados: current.filter(m => m !== moduloKey) };
      } else {
        return { ...prev, modulos_seleccionados: [...current, moduloKey] };
      }
    });
  };

  const filteredProspectos = useMemo(() => {
    return prospectos.filter(p => {
      const matchesSearch = 
        p.nombre_negocio?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.folio?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.contacto_nombre?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = filterStatus === 'all' || p.status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [prospectos, searchQuery, filterStatus]);

  const formatCurrency = (amount, currency = 'MXN') => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: currency === 'USD' ? 'USD' : 'MXN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="prospectos-page">
      {/* Header V4.3 - ADN Fluxo Co */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 pt-4">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <div className="flex items-center gap-2 mb-3">
            <Calculator className="w-5 h-5 text-blue-500" />
            <span className="text-[10px] uppercase tracking-[0.4em] font-black text-blue-500/80">Inteligencia de Negocios</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-white font-outfit">
            Fluxo <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">Quotes</span>
          </h1>
          <p className="text-slate-500 mt-2 font-medium italic text-sm">Generación de cotizaciones dinámicas y conversión de prospectos en ecosistemas operativos</p>
        </motion.div>

        <Button 
          onClick={() => setIsCreateOpen(true)}
          className="group relative overflow-hidden h-12 px-6 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-500/20 transition-all duration-300 hover:shadow-blue-500/30 hover:scale-[1.02]"
          data-testid="new-quote-btn"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-400/0 via-white/10 to-blue-400/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
          <Plus className="h-5 w-5 mr-2 relative z-10" />
          <span className="relative z-10">Nueva Cotización</span>
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <FileText className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total Cotizaciones</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/10">
                  <Clock className="h-5 w-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.pendientes}</p>
                  <p className="text-xs text-muted-foreground">Pendientes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.aprobados}</p>
                  <p className="text-xs text-muted-foreground">Aprobados</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-cyan-500/10">
                  <TrendingUp className="h-5 w-5 text-cyan-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{formatCurrency(stats.mrr_total_mxn)}</p>
                  <p className="text-xs text-muted-foreground">MRR Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 group">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-emerald-400 transition-colors" />
          <Input
            placeholder="Buscar por negocio, folio o contacto..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-950/50 border-white/5 rounded-xl h-11 text-white placeholder:text-slate-600 focus:ring-0 focus:border-emerald-500/40 focus:shadow-[0_0_20px_rgba(16,185,129,0.15)] transition-all duration-300"
            data-testid="search-input"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px] bg-background border-border" data-testid="status-filter">
            <SelectValue placeholder="Filtrar por estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="pendiente">Pendientes</SelectItem>
            <SelectItem value="aprobado">Aprobados</SelectItem>
            <SelectItem value="rechazado">Rechazados</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
            </div>
          ) : filteredProspectos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No hay cotizaciones</p>
              <Button 
                variant="link" 
                onClick={() => setIsCreateOpen(true)}
                className="text-cyan-500"
              >
                Crear primera cotización
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>Folio</TableHead>
                  <TableHead>Negocio</TableHead>
                  <TableHead>Contacto</TableHead>
                  <TableHead className="text-right">Mensual</TableHead>
                  <TableHead className="text-right">Setup</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProspectos.map((prospecto) => {
                  const statusConfig = STATUS_CONFIG[prospecto.status] || STATUS_CONFIG.pendiente;
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <TableRow 
                      key={prospecto.id} 
                      className="border-border"
                      data-testid={`prospecto-row-${prospecto.folio}`}
                    >
                      <TableCell className="font-mono text-sm">{prospecto.folio}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{prospecto.nombre_negocio}</p>
                          <p className="text-xs text-muted-foreground">{prospecto.giro}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{prospecto.contacto_nombre}</p>
                          <p className="text-xs text-muted-foreground">{prospecto.whatsapp_contacto}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(prospecto.total_mensual_mxn)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div>
                          <p className="text-sm">{formatCurrency(prospecto.setup_mxn)}</p>
                          <p className="text-xs text-muted-foreground">{prospecto.setup_nombre}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusConfig.color}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(prospecto.fecha_cotizacion)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadPDF(prospecto.id, prospecto.folio)}
                            title="Descargar PDF"
                            data-testid={`download-pdf-${prospecto.folio}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSendWhatsApp(prospecto.id)}
                            title="Enviar por WhatsApp"
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                          {prospecto.status === 'pendiente' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleApprove(prospecto.id)}
                                className="text-green-500 hover:text-green-600"
                                title="Aprobar"
                                data-testid={`approve-btn-${prospecto.folio}`}
                              >
                                <CheckCircle2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleReject(prospecto.id)}
                                className="text-red-500 hover:text-red-600"
                                title="Rechazar"
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogContent className="sm:max-w-xl bg-[#0B1120] border-cyan-500/20 rounded-[3rem] p-0 overflow-hidden outline-none shadow-2xl">
            <div className="bg-gradient-to-br from-cyan-500/10 to-transparent p-10 max-h-[90vh] overflow-y-auto custom-scrollbar">
              <DialogHeader>
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 flex items-center justify-center border border-cyan-500/20 shadow-inner">
                    <Calculator className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <DialogTitle className="font-outfit text-3xl font-black text-white italic tracking-tighter uppercase">Nueva Cotización</DialogTitle>
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">Inteligencia de Negocios Fluxo Co</p>
                  </div>
                </div>
              </DialogHeader>

              <Tabs defaultValue="datos" className="w-full">
                <TabsList className="bg-white/5 p-1.5 rounded-2xl grid grid-cols-3 gap-2 mb-8 h-auto border border-white/5">
                  <TabsTrigger 
                    value="datos" 
                    className="rounded-xl font-black text-[10px] uppercase tracking-widest py-3 data-[state=active]:bg-cyan-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-cyan-500/20 data-[state=active]:scale-[1.05] transition-all"
                  >
                    Cliente
                  </TabsTrigger>
                  <TabsTrigger 
                    value="modulos" 
                    className="rounded-xl font-black text-[10px] uppercase tracking-widest py-3 data-[state=active]:bg-cyan-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-cyan-500/20 data-[state=active]:scale-[1.05] transition-all"
                  >
                    Módulos
                  </TabsTrigger>
                  <TabsTrigger 
                    value="uso" 
                    className="rounded-xl font-black text-[10px] uppercase tracking-widest py-3 data-[state=active]:bg-cyan-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-cyan-500/20 data-[state=active]:scale-[1.05] transition-all"
                  >
                    Uso
                  </TabsTrigger>
                </TabsList>

            <TabsContent value="datos" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre_negocio">Nombre del Negocio *</Label>
                  <Input
                    id="nombre_negocio"
                    value={formData.nombre_negocio}
                    onChange={(e) => setFormData({...formData, nombre_negocio: e.target.value})}
                    placeholder="Ej: Distribuidora ABC"
                    data-testid="input-nombre-negocio"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="giro">Giro del Negocio * <span className="text-xs text-cyan-500">(aplica plantilla)</span></Label>
                  <Select 
                    value={formData.giro} 
                    onValueChange={handleGiroChange}
                  >
                    <SelectTrigger data-testid="select-giro">
                      <SelectValue placeholder="Selecciona un giro" />
                    </SelectTrigger>
                    <SelectContent>
                      {GIROS_CON_PLANTILLAS.map(giro => (
                        <SelectItem key={giro} value={giro}>
                          {giro}
                          {plantillasGiro && plantillasGiro[giro] && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              ({plantillasGiro[giro].modulos?.length || 0} módulos)
                            </span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.giro && plantillasGiro && plantillasGiro[formData.giro] && (
                    <p className="text-xs text-muted-foreground">
                      📋 {plantillasGiro[formData.giro].descripcion}
                    </p>
                  )}
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contacto_nombre">Nombre del Contacto *</Label>
                  <Input
                    id="contacto_nombre"
                    value={formData.contacto_nombre}
                    onChange={(e) => setFormData({...formData, contacto_nombre: e.target.value})}
                    placeholder="Nombre completo"
                    data-testid="input-contacto-nombre"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contacto_puesto">Puesto</Label>
                  <Input
                    id="contacto_puesto"
                    value={formData.contacto_puesto}
                    onChange={(e) => setFormData({...formData, contacto_puesto: e.target.value})}
                    placeholder="Ej: Director, Gerente"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="whatsapp_contacto">WhatsApp *</Label>
                  <Input
                    id="whatsapp_contacto"
                    value={formData.whatsapp_contacto}
                    onChange={(e) => setFormData({...formData, whatsapp_contacto: e.target.value})}
                    placeholder="+52 55 1234 5678"
                    data-testid="input-whatsapp"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Correo Electrónico *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="contacto@empresa.com"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pais">País</Label>
                  <Select 
                    value={formData.pais} 
                    onValueChange={(v) => setFormData({...formData, pais: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="México">México</SelectItem>
                      <SelectItem value="Colombia">Colombia</SelectItem>
                      <SelectItem value="Chile">Chile</SelectItem>
                      <SelectItem value="Perú">Perú</SelectItem>
                      <SelectItem value="Argentina">Argentina</SelectItem>
                      <SelectItem value="USA">Estados Unidos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="moneda">Moneda Preferida</Label>
                  <Select 
                    value={formData.moneda} 
                    onValueChange={(v) => setFormData({...formData, moneda: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MXN">MXN (Pesos)</SelectItem>
                      <SelectItem value="USD">USD (Dólares)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="modulos" className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground">
                Selecciona los módulos que el cliente necesita. Cada módulo tiene un costo mensual adicional.
              </p>
              
              {modulosConfig && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(modulosConfig.modulos).map(([key, modulo]) => (
                    <div
                      key={key}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        formData.modulos_seleccionados.includes(key)
                          ? 'border-cyan-500 bg-cyan-500/10'
                          : 'border-border hover:border-muted-foreground'
                      }`}
                      onClick={() => toggleModulo(key)}
                      data-testid={`modulo-${key}`}
                    >
                      <Checkbox
                        checked={formData.modulos_seleccionados.includes(key)}
                        onCheckedChange={() => toggleModulo(key)}
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{modulo.nombre}</p>
                        <p className="text-xs text-muted-foreground">
                          +${modulo.precio}/mes
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <Separator />

              <div className="space-y-2">
                <Label>¿Requiere integración especial?</Label>
                <Select 
                  value={formData.integracion_externa} 
                  onValueChange={(v) => setFormData({...formData, integracion_externa: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No requiere</SelectItem>
                    <SelectItem value="sap">SAP</SelectItem>
                    <SelectItem value="erp">Otro ERP</SelectItem>
                    <SelectItem value="custom">Integración personalizada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>

            <TabsContent value="uso" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="usuarios_internos">Usuarios Internos</Label>
                  <Input
                    id="usuarios_internos"
                    type="number"
                    min="1"
                    value={formData.usuarios_internos}
                    onChange={(e) => setFormData({...formData, usuarios_internos: parseInt(e.target.value) || 1})}
                    data-testid="input-usuarios"
                  />
                  <p className="text-xs text-muted-foreground">
                    Personas que usarán el dashboard (admin, operadores, etc.)
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mensajes_dia_estimado">Mensajes por Día (estimado)</Label>
                  <Input
                    id="mensajes_dia_estimado"
                    type="number"
                    min="10"
                    step="10"
                    value={formData.mensajes_dia_estimado}
                    onChange={(e) => setFormData({...formData, mensajes_dia_estimado: parseInt(e.target.value) || 10})}
                    data-testid="input-mensajes"
                  />
                  <p className="text-xs text-muted-foreground">
                    Cantidad aproximada de mensajes entrantes y salientes por día
                  </p>
                </div>
              </div>

              <Card className="bg-muted/30">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Sparkles className="h-5 w-5 text-cyan-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Tip de Estimación</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Un negocio pequeño típicamente maneja 30-50 mensajes/día. 
                        Negocios medianos 100-200, y empresas grandes 500+.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <div className="pt-10">
            <button 
              onClick={handlePreview}
              disabled={submitting}
              className="group relative overflow-hidden w-full h-16 bg-cyan-600 hover:bg-cyan-500 text-white font-black text-xs uppercase tracking-[0.3em] rounded-2xl transition-all flex items-center justify-center gap-3 shadow-xl shadow-cyan-500/20 active:scale-[0.98] disabled:opacity-50"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/0 via-white/10 to-cyan-400/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
              {submitting ? (
                <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
              ) : (
                <Calculator className="w-5 h-5 relative z-10 group-hover:scale-110 transition-transform" /> 
              )}
              <span className="relative z-10">{submitting ? 'Calculando...' : 'Generar Cotización Maestra'}</span>
            </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-2xl bg-background border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-cyan-500" />
              Vista Previa de Cotización
            </DialogTitle>
          </DialogHeader>

          {previewData && (
            <div className="space-y-4">
              {/* Precio mensual regular */}
              <Card className="bg-cyan-500/10 border-cyan-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Mensual</p>
                      <p className="text-3xl font-bold text-cyan-500">
                        {formatCurrency(previewData.total_mensual_mxn)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        ~{formatCurrency(previewData.total_mensual_usd, 'USD')} USD
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Setup ({previewData.setup_nombre})</p>
                      <p className="text-xl font-bold">
                        {formatCurrency(previewData.setup_mxn)}
                      </p>
                      <p className="text-xs text-muted-foreground">único pago</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Descuento Plan Anual */}
              <Card className="bg-green-500/10 border-green-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600 font-medium flex items-center gap-1">
                        <Sparkles className="h-4 w-4" />
                        Plan Anual (10% descuento)
                      </p>
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(previewData.total_con_descuento_anual_mxn)}/mes
                      </p>
                      <p className="text-sm text-muted-foreground">
                        ~{formatCurrency(previewData.total_con_descuento_anual_usd, 'USD')} USD/mes
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Descuento mensual</p>
                      <p className="text-lg font-medium text-green-600">
                        -{formatCurrency(previewData.descuento_anual_mxn)}
                      </p>
                      <p className="text-xs text-green-600">
                        💰 Ahorras {formatCurrency(previewData.ahorro_anual_mxn)}/año
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-2">
                <h4 className="font-medium text-sm">Desglose:</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between py-1 border-b border-border">
                    <span className="text-muted-foreground">Conexión base</span>
                    <span>{formatCurrency(previewData.conexion_base)}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-border">
                    <span className="text-muted-foreground">
                      Usuarios ({formData.usuarios_internos} × $150)
                    </span>
                    <span>{formatCurrency(previewData.costo_usuarios)}</span>
                  </div>
                  {previewData.modulos_desglose?.map(mod => (
                    <div key={mod.key} className="flex justify-between py-1 border-b border-border">
                      <span className="text-muted-foreground">{mod.nombre}</span>
                      <span>{formatCurrency(mod.precio)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between py-1 border-b border-border">
                    <span className="text-muted-foreground">
                      Bloques WA ({previewData.bloques_wa} × $300)
                    </span>
                    <span>{formatCurrency(previewData.costo_wa_bloques)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-border font-medium">
                    <span>Total Mensual</span>
                    <span>{formatCurrency(previewData.total_mensual_mxn)}</span>
                  </div>
                  <div className="flex justify-between py-1 text-green-600">
                    <span>Plan anual (10% desc.)</span>
                    <span>-{formatCurrency(previewData.descuento_anual_mxn)}</span>
                  </div>
                  <div className="flex justify-between py-2 font-bold text-green-600">
                    <span>Total con descuento anual</span>
                    <span>{formatCurrency(previewData.total_con_descuento_anual_mxn)}/mes</span>
                  </div>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                * Aproximadamente {previewData.conversaciones_mes_estimado?.toLocaleString()} conversaciones/mes
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPreviewOpen(false)}>
              Modificar
            </Button>
            <Button 
              onClick={handleCreate}
              className="bg-green-600 hover:bg-green-700"
              disabled={submitting}
              data-testid="confirm-create-btn"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              Crear Cotización
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Result Dialog */}
      <Dialog open={isApproveOpen} onOpenChange={setIsApproveOpen}>
        <DialogContent className="bg-background border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-500">
              <CheckCircle2 className="h-5 w-5" />
              ¡Tenant Creado Exitosamente!
            </DialogTitle>
          </DialogHeader>

          {approvalResult && (
            <div className="space-y-4">
              <p className="text-muted-foreground">{approvalResult.message}</p>
              
              <Card className="bg-muted/30">
                <CardContent className="p-4 space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Portal de Acceso</p>
                    <p className="font-mono text-sm">{approvalResult.portal_url}</p>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Credenciales Generadas ({approvalResult.total_usuarios} usuarios)</p>
                    <div className="space-y-2">
                      {approvalResult.usuarios_creados?.map((user, idx) => (
                        <div key={idx} className="text-sm bg-background p-2 rounded">
                          <p><strong>{user.role}:</strong> {user.email}</p>
                          <p className="font-mono text-xs">Password: {user.password}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <p className="text-xs text-muted-foreground">Módulos Activos</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {approvalResult.modulos_activos?.map(mod => (
                        <Badge key={mod} variant="secondary" className="text-xs">
                          {mod}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setIsApproveOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProspectosPage;
